import { useState } from 'react'
import { Download, History, RotateCcw } from 'lucide-react'

import {
  Avatar,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  FilterPills,
  LoadingSkeleton,
  LockChip,
  Money,
  PageHeader,
  PinSheet,
  QueryBoundary,
  RowAction,
  SearchInput,
  Section,
  useSearchTerm,
  useToast,
  useUrlFilter,
  usePriceLock,
  type Column,
  type FilterPill,
} from '../../../components'
import { paisaToRupees, todayIso } from '@cattle/money'

import { useOwnerOverview, type ActivityEntry } from '../../../fixtures'
import { t } from '../../../lib/i18n'

/**
 * O-04 · Who changed what — the immutable audit trail.
 *
 * There is NO edit and NO delete control on this screen, including for the
 * owner: a log anyone can tidy is not a log. The single exception is Restore on
 * a deleted record, and that restores the record, it does not alter the entry.
 *
 * Was and Now are their own columns because "Asif edited an animal" is useless.
 * Both go through `gate()`, so Price Lock reaches inside the audit trail too.
 */

const FILTERS = ['all', 'money', 'people', 'deleted'] as const
type FilterValue = (typeof FILTERS)[number]

const KIND_FOR: Record<Exclude<FilterValue, 'all'>, ActivityEntry['kind']> = {
  money: 'MONEY',
  people: 'PEOPLE',
  deleted: 'DELETED',
}

/** One CSV cell. Quoted always, so a comma in a note cannot shift a column. */
const cell = (value: string | number | undefined): string =>
  value === undefined ? '""' : `"${String(value).replace(/"/g, '""')}"`

export function HistoryScreen() {
  const overview = useOwnerOverview()
  const toast = useToast()
  const { gate, locked } = usePriceLock()
  const [pinOpen, setPinOpen] = useState(false)
  const [filter, setFilter] = useUrlFilter<FilterValue>('show', 'all', FILTERS)
  const term = useSearchTerm()

  /** Whether a row survives the pills and the search box. */
  const matches = (entry: ActivityEntry): boolean => {
    if (filter !== 'all' && entry.kind !== KIND_FOR[filter]) return false
    if (!term) return true
    const haystack = `${entry.person} ${entry.what} ${entry.wasText ?? ''} ${
      entry.nowText ?? ''
    }`.toLowerCase()
    return haystack.includes(term)
  }

  const rows = (overview.data?.activity ?? []).filter(matches)

  /*
    THE EXPORT.

    CONTRACT GAP: there is no audit-log export endpoint. O4 is `GET /audit-log`
    and nothing more, and `GET /reports/export` exports the book — animals,
    expenses, sales — not who changed what. So the file is written here, from
    the rows on the screen, and the toast says exactly that rather than
    implying the server prepared anything.

    Money goes through `gate()` like every other figure on this page. An export
    that carried the amounts a locked screen is hiding would be the one way
    round Price Lock, and it would be a file — the copy that outlives the
    session.
  */
  const exportCsv = () => {
    if (rows.length === 0) {
      toast.show(t('o.history.exportEmpty'), 'info')
      return
    }

    const money = (paisa: number | undefined): string => {
      const shown = gate(paisa)
      return shown === undefined ? '' : String(paisaToRupees(shown))
    }

    const lines = [
      [
        t('label.when'),
        t('label.who'),
        t('label.role'),
        t('o.overview.colWhatChanged'),
        t('label.was'),
        t('label.now'),
      ].map(cell),
      ...rows.map((entry) =>
        [
          entry.at,
          entry.person,
          t(`role.${entry.role}`),
          entry.what,
          entry.wasPaisa !== undefined ? money(entry.wasPaisa) : (entry.wasText ?? ''),
          entry.nowPaisa !== undefined ? money(entry.nowPaisa) : (entry.nowText ?? ''),
        ].map(cell),
      ),
    ]

    // The BOM is what makes Excel open Urdu names and the rupee sign correctly.
    const blob = new Blob([`﻿${lines.map((line) => line.join(',')).join('\r\n')}`], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `${t('o.history.exportFilename')}-${todayIso()}.csv`
    anchor.click()
    URL.revokeObjectURL(url)

    toast.show(t('o.history.exported', { count: rows.length }))
    // Said afterwards, because the file is already short of two columns and the
    // reader needs to know before they send it to an accountant.
    if (locked) toast.show(t('o.history.exportLocked'), 'info')
  }

  const pills: FilterPill<FilterValue>[] = [
    { value: 'all', label: t('o.history.filterEverything') },
    { value: 'money', label: t('o.history.filterMoney') },
    { value: 'people', label: t('o.history.filterPeople') },
    { value: 'deleted', label: t('o.history.filterDeleted') },
  ]

  const changeCell = (paisa: number | undefined, text: string | undefined, deleted: boolean) => {
    if (paisa !== undefined) return <Money paisa={gate(paisa)} />
    if (text) {
      return (
        <span className={deleted ? 'font-display font-bold text-danger' : 'font-semibold text-dark'}>
          {text}
        </span>
      )
    }
    return <span className="text-light">{t('common.none')}</span>
  }

  const columns: Column<ActivityEntry>[] = [
    {
      id: 'when',
      header: t('label.when'),
      width: '130px',
      cell: (entry) => <span className="text-sub">{entry.at}</span>,
    },
    {
      id: 'who',
      header: t('label.who'),
      width: '180px',
      cell: (entry) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={entry.person} size="sm" />
          <div className="min-w-0">
            <div className="font-semibold text-dark">{entry.person}</div>
            <div className="text-[11.5px] text-muted">{t(`role.${entry.role}`)}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'what',
      header: t('o.overview.colWhatChanged'),
      cell: (entry) => <span className="text-sub">{entry.what}</span>,
    },
    {
      id: 'was',
      header: t('label.was'),
      numeric: true,
      cell: (entry) => changeCell(entry.wasPaisa, entry.wasText, false),
    },
    {
      id: 'now',
      header: t('label.now'),
      numeric: true,
      cell: (entry) => changeCell(entry.nowPaisa, entry.nowText, entry.kind === 'DELETED'),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('o.history.title')}
        subtitle={t('o.history.subtitle')}
        actions={
          <>
            <LockChip onRequestUnlock={() => setPinOpen(true)} />
            <Button icon={<Download className="size-4" />} onClick={exportCsv}>
              {t('act.export')}
            </Button>
          </>
        }
      />

      {/*
        A "Last 7 days" button used to sit here. `GET /audit-log` is fetched
        with no `from`/`to`, so no range was ever applied and pressing it only
        showed a toast repeating its own label — a date filter that named a
        window the screen did not have. It is gone; what IS shown is said under
        the table instead.
      */}
      <FilterBar>
        <SearchInput placeholder={t('o.history.searchPlaceholder')} />
        <FilterPills
          pills={pills}
          value={filter}
          onChange={setFilter}
          label={t('o.history.title')}
        />
      </FilterBar>

      <QueryBoundary
        query={overview}
        skeleton={<LoadingSkeleton variant="table" rows={7} />}
        isEmpty={(data) => data.activity.length === 0}
        empty={
          <EmptyState
            icon={<History className="size-6" />}
            title={t('o.history.emptyTitle')}
            body={t('o.history.emptyBody')}
          />
        }
      >
        {() => {
          // Filtered once, above, so the file and the table can never disagree
          // about which rows the reader is looking at.
          return (
            <>
              <Section>
                <DataTable
                  columns={columns}
                  rows={rows}
                  rowKey={(entry) => entry.id}
                  caption={t('o.history.title')}
                  rowActions={(entry) =>
                    entry.restorable ? (
                      <RowAction
                        label={t('o.history.restore')}
                        icon={<RotateCcw className="size-full" />}
                        onClick={() => toast.show(t('o.history.restore'), 'info')}
                      />
                    ) : null
                  }
                  empty={
                    <EmptyState
                      icon={<History className="size-6" />}
                      title={t('table.noResults')}
                      body={t('o.history.emptyBody')}
                    />
                  }
                />
              </Section>

              <p className="mt-3 text-center text-xs text-muted">{t('o.history.immutable')}</p>
              <p className="mt-1 text-center text-xs text-muted">{t('o.history.noRange')}</p>
            </>
          )
        }}
      </QueryBoundary>

      <PinSheet open={pinOpen} onClose={() => setPinOpen(false)} />
    </>
  )
}
